import React, { useEffect, useRef, useState } from 'react'
import { Link as RouterLink, useNavigate } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { FaArrowLeft } from 'react-icons/fa'
import * as THREE from 'three'
import CryptoJS from 'crypto-js'
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js'
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js'
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js'
import { RoomEnvironment } from 'three/examples/jsm/environments/RoomEnvironment.js'
import VisibilityIcon from '@mui/icons-material/Visibility'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import LockOpenIcon from '@mui/icons-material/LockOpen'
import OtpInput from 'react-otp-input'
import {
  Box,
  Button,
  Divider,
  IconButton,
  InputAdornment,
  Link,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import MailOutlineRoundedIcon from '@mui/icons-material/MailOutlineRounded'
import { Spinner } from 'react-bootstrap'
import { toast } from '../core/lib/toastAlert'
import Config from '../core/config'
import { makeRequest } from '../core/services/v1/request'
import CaptchaModal from '../Component/Captcha/CaptchaModal'
import { setCookie } from '../core/helper/cookie'
import { useContextData } from '../core/context'
import { showEmail } from '../core/helper/date-format'
import { handleGoogleSignIn } from '../Component/Auth/GoogleAuth'
import TelegramLogin from '../Component/Auth/TelegramAuth'
const SECRET_KEY = Config.ENCKEY

/* =========================
   Three.js scene (polished)
========================= */
function useUniticLeftScene(mountRef) {
  useEffect(() => {
    const mount = mountRef.current
    if (!mount) return
    const isWebGLAvailable = () => {
      try {
        const c = document.createElement('canvas')
        if (window.WebGL2RenderingContext && c.getContext('webgl2')) return true
        if (c.getContext('webgl')) return true
        if (c.getContext('experimental-webgl')) return true
        return false
      } catch {
        return false
      }
    }
    if (!isWebGLAvailable()) return

    let renderer
    try {
      renderer = new THREE.WebGLRenderer({
        antialias: true,
        alpha: true,
        powerPreference: 'high-performance',
      })
    } catch {
      return
    }
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
    renderer.setClearColor(0x000000, 0)
    // modern three color management
    renderer.outputColorSpace = THREE.SRGBColorSpace
    renderer.toneMapping = THREE.ACESFilmicToneMapping
    mount.appendChild(renderer.domElement)

    // ---------- Scene ----------
    const scene = new THREE.Scene()

    // subtle fog helps “cinematic depth”
    scene.fog = new THREE.Fog(0x020617, 8.0, 18.0)

    // ---------- Camera ----------
    const camera = new THREE.PerspectiveCamera(32, 1, 0.1, 60)
    camera.position.set(0.2, 0.2, 10.8)

    // ---------- Environment (premium reflections) ----------
    const pmrem = new THREE.PMREMGenerator(renderer)
    const envTex = pmrem.fromScene(new RoomEnvironment(renderer), 0.05).texture
    scene.environment = envTex

    // ---------- Lights (match expected: soft, controlled) ----------
    const ambient = new THREE.AmbientLight(0xffffff, 0.22)
    scene.add(ambient)

    const key = new THREE.DirectionalLight(0xffffff, 1.15)
    key.position.set(5, 3.2, 7)
    key.castShadow = false
    scene.add(key)

    const fill = new THREE.DirectionalLight(0x1db0a1, 0.55)
    fill.position.set(-6, 1.8, 3)
    scene.add(fill)

    const rim = new THREE.PointLight(0x1db0a1, 0.9, 20, 2)
    rim.position.set(-3.5, 1.2, -2)
    scene.add(rim)

    // ---------- Root group (composition: left + down like expected) ----------
    const root = new THREE.Group()
    root.position.set(-1.25, -0.65, 0) // key: pushes composition to left
    root.scale.set(0.65, 0.65, 0.65) // Make the whole composition smaller
    scene.add(root)

    // ---------- Materials ----------
    // Rings: dark matte-metal with slight clearcoat look
    const ringMat = new THREE.MeshPhysicalMaterial({
      color: new THREE.Color('#070c12'),
      metalness: 0.55,
      roughness: 0.32,
      clearcoat: 0.35,
      clearcoatRoughness: 0.5,
      envMapIntensity: 0.55,
    })

    const ringMat2 = new THREE.MeshPhysicalMaterial({
      color: new THREE.Color('#0a1018'),
      metalness: 0.45,
      roughness: 0.55,
      clearcoat: 0.2,
      clearcoatRoughness: 0.65,
      envMapIntensity: 0.35,
      transparent: true,
      opacity: 0.95,
    })

    // Blob: dark, smooth, with subtle teal sheen (real lighting)
    const blobMat = new THREE.MeshPhysicalMaterial({
      color: new THREE.Color('#03070c'),
      metalness: 0.18,
      roughness: 0.42,
      emissive: new THREE.Color('#072a26'),
      emissiveIntensity: 0.25,
      envMapIntensity: 0.35,
      clearcoat: 0.2,
      clearcoatRoughness: 0.5,
    })

    // ---------- Geometry ----------
    const torus = new THREE.Mesh(
      new THREE.TorusGeometry(3.15, 0.2, 24, 320),
      ringMat,
    )
    torus.rotation.set(0.25, 0.25, 0.12)
    root.add(torus)

    const ring2 = new THREE.Mesh(
      new THREE.TorusGeometry(2.55, 0.06, 22, 280),
      ringMat2,
    )
    ring2.scale.set(1.35, 0.56, 1.0) // ellipse vibe
    ring2.rotation.set(-0.7, 0.12, -0.42)
    root.add(ring2)

    // small outer “frame ring” faint (like screenshot edge ring)
    const frameRing = new THREE.Mesh(
      new THREE.TorusGeometry(3.55, 0.02, 14, 220),
      new THREE.MeshStandardMaterial({
        color: new THREE.Color('#0b1722'),
        metalness: 0.2,
        roughness: 0.9,
        transparent: true,
        opacity: 0.35,
      }),
    )
    frameRing.rotation.set(0.2, 0.2, 0.05)
    root.add(frameRing)

    // Blocks / notches on ring (more subtle, match expected)
    const blockGeo = new THREE.BoxGeometry(0.42, 0.26, 0.2)
    const blockMat = new THREE.MeshPhysicalMaterial({
      color: new THREE.Color('#060b11'),
      metalness: 0.5,
      roughness: 0.4,
      clearcoat: 0.25,
      clearcoatRoughness: 0.7,
      envMapIntensity: 0.45,
    })

    function addBlock(angle, radius = 3.15, z = 0.0) {
      const b = new THREE.Mesh(blockGeo, blockMat)
      const x = Math.cos(angle) * radius
      const y = Math.sin(angle) * radius
      b.position.set(x, y, z)
      b.lookAt(0, 0, 0)
      b.rotation.z += 0.55
      root.add(b)
      return b
    }

    const blockA = addBlock(2.35, 3.15, 0.02)
    const blockB = addBlock(5.05, 3.15, -0.02)

    // Blob geometry (smooth circle/sphere now)
    const blobGeo = new THREE.SphereGeometry(1.85, 64, 64)
    const blob = new THREE.Mesh(blobGeo, blobMat)
    blob.position.set(0, 0, 0)
    root.add(blob)

    // ---------- No vertex displacement (keep it a perfect sphere) ----------
    blobMat.needsUpdate = true

    // ---------- Glow particles (few + bloom) ----------
    const spriteTex = new THREE.TextureLoader().load(
      // small radial dot (data uri)
      'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAEAAAAAQCAYAAABAfUpcAAAACXBIWXMAAAsSAAALEgHS3X78AAABHUlEQVR4nO2XwQ3CMAxF5xQkQJpA0gSgCSRNQJIApAkgTQDJH6l3a9bO2s8gk0q8oVxv4R4o2k0F+8gK2w1o5p6dXyV8oGmQd5zqE2dWJtXkQWn0wYhZcHnQz2jBf2p0bQxJ0wQqjzJx0m0xj3mA0q1Kc3hQdZpQm5cQxgk3zYB7cGmO4kG9wq1WvJbJz1o7m3wC8d3Y1Q8xYcGm3f4mBq9m1g8Qw8q3oF+qv8b9wJ9i2mQm1u8u9o0n4lB3+g5yQqQ0Zb4e3w8Y0xqg4c6m9oWf5oGm9m6YVjz0a8w8Q8H2g3f8wAAAABJRU5ErkJggg==',
    )
    spriteTex.colorSpace = THREE.SRGBColorSpace

    const glowMat = new THREE.SpriteMaterial({
      map: spriteTex,
      color: new THREE.Color('#bff7f0'),
      transparent: true,
      opacity: 0.95,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    })

    const p1 = new THREE.Sprite(glowMat.clone())
    p1.scale.set(0.35, 0.35, 0.35)
    p1.position.set(1.05, 0.65, 0.75)
    root.add(p1)

    const p2 = new THREE.Sprite(glowMat.clone())
    p2.scale.set(0.22, 0.22, 0.22)
    p2.position.set(1.25, 0.35, 0.62)
    root.add(p2)

    // ---------- Postprocessing (Bloom) ----------
    const composer = new EffectComposer(renderer)
    composer.addPass(new RenderPass(scene, camera))
    const bloom = new UnrealBloomPass(new THREE.Vector2(1, 1), 0.55, 0.8, 0.9)
    composer.addPass(bloom)

    // ---------- Resize ----------
    const resize = () => {
      const w = mount.clientWidth
      const h = mount.clientHeight
      camera.aspect = w / h
      camera.updateProjectionMatrix()
      renderer.setSize(w, h, false)
      composer.setSize(w, h)
      bloom.setSize(w, h)
    }
    resize()

    const onResize = () => resize()
    window.addEventListener('resize', onResize)

    // ---------- Animate (expected: slow, premium, controlled) ----------
    const clock = new THREE.Clock()
    let raf = 0

    const animate = () => {
      const t = clock.getElapsedTime()

      // Slow choreography (less dramatic than before)
      root.rotation.z = t * 0.06
      torus.rotation.y = 0.25 + t * 0.07
      ring2.rotation.z = -0.42 - t * 0.05

      // tiny camera drift (parallax), keep subtle
      camera.position.x = 0.2 + Math.sin(t * 0.18) * 0.07
      camera.position.y = 0.2 + Math.sin(t * 0.32) * 0.05
      camera.lookAt(-1.2, -0.45, 0)

      // glow twinkle
      p1.material.opacity = 0.45 + (Math.sin(t * 2.1) * 0.5 + 0.5) * 0.5
      p2.material.opacity = 0.35 + (Math.sin(t * 1.7 + 1.1) * 0.5 + 0.5) * 0.55

      composer.render()
      raf = requestAnimationFrame(animate)
    }
    raf = requestAnimationFrame(animate)

    // ---------- Cleanup ----------
    return () => {
      cancelAnimationFrame(raf)
      window.removeEventListener('resize', onResize)

      composer?.dispose?.()
      pmrem?.dispose?.()

      scene.traverse((obj) => {
        if (obj.geometry) obj.geometry.dispose?.()
        if (obj.material) {
          if (Array.isArray(obj.material))
            obj.material.forEach((m) => m.dispose?.())
          else obj.material.dispose?.()
        }
      })
      spriteTex?.dispose?.()
      renderer.dispose()

      if (renderer.domElement?.parentNode) {
        renderer.domElement.parentNode.removeChild(renderer.domElement)
      }
    }
  }, [mountRef])
}

/* =========================
   SignIn (MUI layout)
========================= */
const SignIn = () => {
  const threeMountRef = useRef(null)
  useUniticLeftScene(threeMountRef)
  const [eye, setEye] = useState(false)
  const [isLoading, setisLoading] = useState(false)
  let captchaValue = useRef(null)
  const [openCaptcha, setOpenCaptcha] = useState(false)
  const { setUserProfile } = useContextData()
  const navigate = useNavigate()
  const [formData, setFormData] = useState({
    email: '',
    captcha_value: '',
    otp: '',
    password: '',
    step: 0,
  })
  const [showLogin, setshowLogin] = useState(true)
  const [otpShow, setotpShow] = useState(0)
  const setStateFunc = (author, value) => {
    setFormData((prev) => ({ ...prev, [author]: value }))
  }
  const handleOpenCaptcha = () => setOpenCaptcha(true)
  const handleCloseCaptcha = () => {
    if (captchaValue.current?.getValue()) {
      setOpenCaptcha(false)
      signin()
    }
  }
  const [timer_resendOtp, setTimer_resendOtp] = useState(0)
  const handleEmailBlur = (e) => {
    if (e.target.value) {
      setStateFunc('step', 1)
    } else {
      setStateFunc('step', 0)
    }
  }
  const signin = async (isResend = 0) => {
    setisLoading(true)
    let payload = Object.assign(formData, {
      isResend: isResend,
      captcha_value: captchaValue.current?.getValue() || '',
    })
    if (payload?.step === 1 && !payload?.captcha_value) {
      setisLoading(false)
      toast({ type: 'error', message: 'Please verify captcha' })
      return false
    }
    captchaValue.current?.reset()

    if (isResend === 1) {
      let valuesCopy = Object.assign({}, payload)
      valuesCopy.otp = ''
      payload = valuesCopy
    }

    const encryptedData = CryptoJS.AES.encrypt(
      JSON.stringify(payload),
      SECRET_KEY,
    ).toString()
    const params = {
      url: `${Config.V1_API_URL}user/login`,
      method: 'POST',
      data: { data: encryptedData },
    }
    const response = await makeRequest(params)
    setisLoading(false)
    let type = 'error'
    if (response.status) {
      type = 'success'
      setotpShow(response.type)
      setshowLogin(false)
      setStateFunc('step', 2)
      if (!response.type) {
        setCookie('userToken', response.token)
        setUserProfile()
        navigate('/my/dashboard')
      } else {
        setTimer_resendOtp(Config.timer.resendOtp)
      }
    }
    toast({
      type,
      message: response?.message ? response?.message : response?.Msg,
    })
  }
  const handleSubmit = async (e, isResend = 0) => {
    e?.preventDefault()
    if (formData?.step === 1) {
      return handleOpenCaptcha()
    }
    if (!showLogin) {
      if (formData.otp == '' && isResend !== 1) {
        return toast({ type: 'error', message: 'Please enter valid Code' })
      }
    }
    return signin(isResend)
  }
  useEffect(() => {
    timer_resendOtp > 0 &&
      setTimeout(() => setTimer_resendOtp(timer_resendOtp - 1), 1000)
  }, [timer_resendOtp])

  const signWithGoogle = async () => {
    try {
      setisLoading(true)
      const gAuth = await handleGoogleSignIn('login')
      if (gAuth) {
        let type = 'error'
        if (gAuth?.status) {
          type = 'success'
          setisLoading(false)
          toast({ type, message: gAuth?.message })
          if (gAuth.type === 0) {
            setCookie('userToken', gAuth.token)
            setUserProfile()
            navigate('/my/dashboard')
          }
          if (gAuth.type === 1) {
            return navigate('/register')
          }
          if (gAuth.type === 2) {
            return navigate('/register', {
              state: {
                from: 'login',
                idToken: gAuth?.data?.idToken,
                email: gAuth?.data?.email,
                gAuth: gAuth?.data?.isCreated,
                step: gAuth.data?.step,
              },
            })
          }
        }

        toast({ type, message: gAuth?.message })
      } else {
        toast({ type: 'error', message: 'Google Sign-in Failed' })
      }
      setisLoading(false)
    }
    catch (error) {
      toast({ type: 'error', message: 'Google Sign-in Failed' })
      setisLoading(false)
    }
  }
  const handleAuthTele = async (responseData) => {
    setisLoading(true)
    if (responseData) {
      let type = 'error'
      if (responseData?.status) {
        type = 'success'
        setisLoading(false)
        toast({ type, message: responseData?.message })
        if (responseData.type === 0) {
          setCookie('userToken', responseData.token)
          setUserProfile()
          navigate('/my/dashboard')
        }
        if (responseData.type === 1) {
          return navigate('/register')
        }
        if (responseData.type === 2) {
          return navigate('/register', {
            state: {
              from: 'login',
              teleDataToken: responseData?.data?.teleDataToken,
              telegramId: responseData?.data?.telegramId,
              username: responseData?.data?.username,
              teleAuth: responseData?.data?.isCreated,
              step: responseData.data?.step,
            },
          })
        }
      }

      toast({ type, message: responseData?.message })
    } else {
      toast({ type: 'error', message: 'Telegram Sign-in Failed' })
    }
    setisLoading(false)
  }

  return (
    <>
      <Helmet>
        <title>Sign In - Unitic Exchange</title>
        <meta
          name='description'
          content='Sign in to your Unitic Exchange account'
        />
      </Helmet>

      <Box
        sx={{
          minHeight: '100vh',
          width: '100%',
          display: 'flex',
          bgcolor: '#0E1116',
          color: 'common.white',
          overflow: 'hidden',
        }}
      >
        {/* LEFT (Three.js) */}
        <Box
          sx={{
            display: { xs: 'none', lg: 'flex' },
            width: '50%',
            position: 'relative',
            overflow: 'hidden',
            backgroundColor: '#0E1116',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '60%',
              height: '60%',
              borderRadius: '50%',
              background:
                'radial-gradient(circle, rgba(29, 160, 148, 0.08) 0%, rgba(14, 17, 22, 0) 70%)',
              pointerEvents: 'none',
              zIndex: 0,
            }}
          />
          <Link
            component={RouterLink}
            to='/'
            underline='none'
            sx={{
              position: 'absolute',
              top: 16,
              left: 16,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1,
              color: 'rgba(148,163,184,0.9)',
              fontSize: 14,
              '&:hover': { color: 'common.white' },
              zIndex: 20,
            }}
          >
            <Box component={FaArrowLeft} sx={{ fontSize: 14 }} />
            Back to Home
          </Link>

          {/* canvas */}
          <Box
            ref={threeMountRef}
            sx={{
              position: 'absolute',
              inset: 0,
              zIndex: 3,
              '& canvas': {
                width: '100% !important',
                height: '100% !important',
                display: 'block',
                pointerEvents: 'none',
              },
            }}
          />

          {/* subtle vignette to match expected */}
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              zIndex: 4,
              background:
                'radial-gradient(1200px 800px at 50% 50%, rgba(0,0,0,0) 40%, rgba(0,0,0,0.35) 85%, rgba(0,0,0,0.55) 100%)',
              pointerEvents: 'none',
            }}
          />

          {/* bottom text */}
          <Box
            sx={{
              position: 'absolute',
              bottom: 56,
              left: 0,
              right: 0,
              px: 8,
              textAlign: 'center',
              zIndex: 10,
            }}
          >
            <Typography
              sx={{ fontSize: 40, fontWeight: 800, letterSpacing: -0.6 }}
            >
              Trade Smarter, Grow Faster
            </Typography>
            <Typography
              sx={{
                mt: 1.5,
                color: 'rgba(148,163,184,0.9)',
                fontSize: 16,
                maxWidth: 560,
                mx: 'auto',
                lineHeight: 1.7,
              }}
            >
              Access the world's most advanced crypto trading platform with zero
              fees on spot trading.
            </Typography>
          </Box>
        </Box>

        {/* RIGHT (Form) */}
        <Box
          sx={{
            flex: 1,
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            px: { xs: 2.5, sm: 4 },
            py: { xs: 6, sm: 8 },
            bgcolor: '#0E1116',
          }}
        >
          <IconButton
            component={RouterLink}
            to='/'
            sx={{
              display: { xs: 'inline-flex', lg: 'none' },
              position: 'absolute',
              top: 16,
              left: 16,
              color: 'rgba(148,163,184,0.9)',
              '&:hover': { color: 'common.white' },
            }}
          >
            <FaArrowLeft />
          </IconButton>

          <Box sx={{ width: '100%', maxWidth: 448 }}>
            <Box sx={{ textAlign: 'center', mb: 6 }}>
              <img src='/logo.png' alt='Unitic' className='img-fluid img-size' />
            </Box>

            <Paper
              elevation={0}
              sx={{
                borderRadius: 4,
                p: '32px 24px',
                backgroundColor: 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(255,255,255,0.08)',
                backdropFilter: 'blur(20px)',
              }}
            >
              <Box sx={{ textAlign: 'center', mb: 3 }}>
                <Typography
                  component='h1'
                  sx={{
                    color: '#F8FAFC',
                    fontSize: 24,
                    fontWeight: 800,
                    // mb: 0.75,
                  }}
                >
                  Welcome Back
                </Typography>
                <Typography
                  sx={{ fontSize: 13, color: 'rgba(148,163,184,0.9)' }}
                >
                  Sign in to your account to continue trading
                </Typography>
              </Box>

              <Box component='form' onSubmit={handleSubmit}>
                <Stack spacing={2.25}>
                  {showLogin ? (
                    <Box>
                      <Typography
                        sx={{
                          fontSize: 12,
                          fontWeight: 700,
                          color: 'rgba(226,232,240,0.95)',
                          mb: '.5rem !important',
                        }}
                      >
                        Email
                      </Typography>

                      <TextField
                        fullWidth
                        placeholder='name@example.com'
                        type='email'
                        value={formData.email}
                        onChange={(e) => setStateFunc('email', e.target.value)}
                        onBlur={(e) => handleEmailBlur(e)}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position='start'>
                              <MailOutlineRoundedIcon
                                sx={{
                                  color: 'rgba(148,163,184,0.9)',
                                  fontSize: 20,
                                }}
                              />
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          '& .MuiInputBase-root': {
                            height: 40,
                            borderRadius: 3,
                            color: 'white',
                            backgroundColor: 'rgba(255,255,255,0.04)',
                            border: '1px solid rgba(255,255,255,0.08)',
                            transition: 'all 180ms ease',
                          },
                          '& .MuiInputBase-root:hover': {
                            backgroundColor: 'rgba(255,255,255,0.055)',
                            borderColor: 'rgba(255,255,255,0.14)',
                          },
                          '& .MuiInputBase-root.Mui-focused': {
                            backgroundColor: 'rgba(255,255,255,0.06)',
                            borderColor: '#1DAFA1',
                            boxShadow: '0 0 0 3px rgba(29,176,161,0.18)',
                          },
                          '& fieldset': { border: 'none' },
                          '& input::placeholder': {
                            color: 'rgba(148,163,184,0.75)',
                            opacity: 1,
                          },
                          '& input:-webkit-autofill': {
                            WebkitTextFillColor: '#fff',
                            caretColor: '#fff',
                            borderRadius: '12px',
                            transition: 'background-color 9999s ease-in-out 0s',
                          },
                          '& input:-webkit-autofill:focus': {
                            WebkitBoxShadow:
                              '0 0 0 1000px #1a1f2b inset, 0 0 0 3px rgba(29,176,161,0.18)',
                          },
                        }}
                      />
                    </Box>
                  ) : null}
                  {showLogin && formData.step !== 0 ? (
                    <Box sx={{ mt: '1rem !important' }}>
                      {' '}
                      <Typography
                        sx={{
                          fontSize: 12,
                          fontWeight: 700,
                          color: 'rgba(226,232,240,0.95)',
                          mb: '.5rem !important',
                        }}
                      >
                        Password
                      </Typography>
                      <TextField
                        fullWidth
                        placeholder='password'
                        type={eye ? 'text' : 'password'}
                        value={formData.password}
                        onChange={(e) =>
                          setStateFunc('password', e.target.value)
                        }
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position='start'>
                              <LockOpenIcon
                                sx={{
                                  color: 'rgba(148,163,184,0.9)',
                                  fontSize: 20,
                                }}
                              />
                            </InputAdornment>
                          ),
                          endAdornment: (
                            <InputAdornment position='end'>
                              {eye ? (
                                <VisibilityIcon
                                  onClick={() => setEye(() => false)}
                                  sx={{
                                    color: 'rgba(148,163,184,0.9)',
                                    fontSize: 20,
                                    cursor: 'pointer',
                                  }}
                                />
                              ) : (
                                <VisibilityOffIcon
                                  onClick={() => setEye(() => true)}
                                  sx={{
                                    color: 'rgba(148,163,184,0.9)',
                                    fontSize: 20,
                                    cursor: 'pointer',
                                  }}
                                />
                              )}
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          '& .MuiInputBase-root': {
                            height: 40,
                            borderRadius: 3,
                            color: 'white',
                            backgroundColor: 'rgba(255,255,255,0.04)',
                            border: '1px solid rgba(255,255,255,0.08)',
                            transition: 'all 180ms ease',
                          },
                          '& .MuiInputBase-root:hover': {
                            backgroundColor: 'rgba(255,255,255,0.055)',
                            borderColor: 'rgba(255,255,255,0.14)',
                          },
                          '& .MuiInputBase-root.Mui-focused': {
                            backgroundColor: 'rgba(255,255,255,0.06)',
                            borderColor: '#1DAFA1',
                            boxShadow: '0 0 0 3px rgba(29,176,161,0.18)',
                          },
                          '& fieldset': { border: 'none' },
                          '& input::placeholder': {
                            color: 'rgba(148,163,184,0.75)',
                            opacity: 1,
                          },
                          '& input:-webkit-autofill': {
                            WebkitTextFillColor: '#fff',
                            caretColor: '#fff',
                            borderRadius: '12px',
                            transition: 'background-color 9999s ease-in-out 0s',
                          },
                          '& input:-webkit-autofill:focus': {
                            WebkitBoxShadow:
                              '0 0 0 1000px #1a1f2b inset, 0 0 0 3px rgba(29,176,161,0.18)',
                          },
                        }}
                      />
                    </Box>
                  ) : null}
                  {!showLogin ? (
                    <Box>
                      <div className='mx-auto text-center text-light'>
                        <div className='mx-auto text-center'>
                          <p
                            className='text-start mb-3'
                            style={{
                              color: '#C8C8C8',
                              fontFamily:
                                'Roboto, "PingFang SC", -apple-system, BlinkMacSystemFont, "Microsoft YaHei"',
                            }}
                          >
                            {otpShow === 1 ? 'OTP Code' : '2FA Code'} Was Sent
                            to{otpShow === 1 ? ' Email' : ''}{' '}
                            {showEmail(formData.email)}
                            {otpShow === 1 ? '' : ' [Google Auth]'}
                          </p>
                          <OtpInput
                            id='otp'
                            name='otp'
                            value={formData.otp}
                            onChange={(e) => setStateFunc('otp', e)}
                            numInputs={6}
                            inputType='number'
                            shouldAutoFocus={true}
                            containerStyle={{
                              width: '100%',
                              marginLeft: 'auto',
                              marginRight: 'auto',
                              textAlign: 'center',
                            }}
                            inputStyle={{
                              borderRadius: '40px',
                              border: 'none',
                              width: '100%',
                              height: window.innerWidth < 500 ? '45px' : '52px',
                              outline: 'none',
                              fontWeight: '600',
                              fontSize: '22px',
                              fontFamily:
                                'Roboto, "PingFang SC", -apple-system, BlinkMacSystemFont, "Microsoft YaHei"',
                              textAlign: 'center',
                              background: '#1b202a',
                              color: 'white',
                            }}
                            renderSeparator={<span className='mx-2'></span>}
                            renderInput={(props) => <input {...props} />}
                          />

                          <p
                            className='text-end text-danger pt-3'
                            style={{
                              fontFamily:
                                'Roboto, "PingFang SC", -apple-system, BlinkMacSystemFont, "Microsoft YaHei"',
                              cursor: 'pointer',
                              display: otpShow !== 1 ? 'none' : 'block',
                            }}
                            disabled={
                              isLoading || timer_resendOtp > 0 ? true : false
                            }
                            onClick={(e) => {
                              if (isLoading || timer_resendOtp > 0) return
                              handleSubmit(e, 1)
                            }}
                          >
                            Resend OTP{' '}
                            {timer_resendOtp > 0 ? (
                              <span className='timeLeft'>
                                ({timer_resendOtp})
                              </span>
                            ) : (
                              ''
                            )}
                          </p>
                        </div>
                      </div>
                    </Box>
                  ) : null}
                  <Button
                    type='submit'
                    fullWidth
                    disabled={
                      isLoading ||
                      formData.step === 0 ||
                      !formData.password ||
                      formData.email.indexOf('@') === -1
                    }
                    sx={{
                      height: 40,
                      borderRadius: 3,
                      textTransform: 'none',
                      transition: '0.2s',
                      bgcolor: '#1DAFA1',
                      color: 'white',
                      fontWeight: '700 !important',
                      mt: '1.5rem !important',
                      '&:hover': { bgcolor: '#23c5b5' },
                      '&:disabled': {
                        bgcolor: 'rgb(29, 160, 148)',
                        color: 'white',
                        cursor: 'not-allowed',
                      },
                    }}
                  >
                    {isLoading ? (
                      <Spinner animation='border' size='sm' />
                    ) : showLogin ? (
                      'Continue with Email'
                    ) : (
                      'Submit'
                    )}
                  </Button>
                  <CaptchaModal
                    openCaptcha={openCaptcha}
                    handleOpenCaptcha={handleOpenCaptcha}
                    handleCloseCaptcha={handleCloseCaptcha}
                    captchaValue={captchaValue}
                  />
                  <Stack direction='row' alignItems='center' spacing={2}>
                    <Divider
                      sx={{ flex: 1, borderColor: 'rgba(255,255,255,0.08)' }}
                    />
                    <Typography
                      sx={{
                        fontSize: 11,
                        fontWeight: 800,
                        color: 'rgba(100,116,139,1)',
                        letterSpacing: 0.6,
                      }}
                    >
                      OR CONTINUE WITH
                    </Typography>
                    <Divider
                      sx={{ flex: 1, borderColor: 'rgba(255,255,255,0.08)' }}
                    />
                  </Stack>

                  <Stack direction='row' spacing={1.5}>
                    <Button
                      type='button'
                      fullWidth
                      startIcon={
                        <svg width='20' height='20' viewBox='0 0 24 24'>
                          <path
                            d='M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z'
                            fill='#4285F4'
                          />
                          <path
                            d='M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z'
                            fill='#34A853'
                          />
                          <path
                            d='M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z'
                            fill='#FBBC05'
                          />
                          <path
                            d='M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z'
                            fill='#EA4335'
                          />
                        </svg>
                      }
                      sx={{
                        height: 40,
                        borderRadius: 3,
                        textTransform: 'none',
                        fontWeight: 800,
                        color: 'white',
                        bgcolor: 'rgba(255,255,255,0.04)',
                        border: '1px solid rgba(255,255,255,0.08)',
                        '&:hover': {
                          bgcolor: 'rgba(255,255,255,0.08)',
                          borderColor: 'rgba(255,255,255,0.16)',
                          transform: 'translateY(-2px)',
                        },
                        transition:
                          'transform 180ms ease, background-color 180ms ease, border-color 180ms ease',
                      }}
                      onClick={() => signWithGoogle()}
                    >
                      Google
                    </Button>
                    <TelegramLogin
                      type={'login'}
                      handleAuthTele={handleAuthTele}
                    >
                      <Button
                        type='button'
                        fullWidth
                        startIcon={
                          <img src='./assets/socialLogos/telegram.svg' alt='' style={{ width: '20px', height: '20px' }} />
                        }
                        sx={{
                          height: 40,
                          borderRadius: 3,
                          textTransform: 'none',
                          fontWeight: 800,
                          color: 'white',
                          bgcolor: 'rgba(255,255,255,0.04)',
                          border: '1px solid rgba(255,255,255,0.08)',
                          '&:hover': {
                            bgcolor: 'rgba(255,255,255,0.08)',
                            borderColor: 'rgba(255,255,255,0.16)',
                            transform: 'translateY(-2px)',
                          },
                          transition:
                            'transform 180ms ease, background-color 180ms ease, border-color 180ms ease',
                        }}
                      >
                        Telegram
                      </Button>
                    </TelegramLogin>
                  </Stack>

                  <Typography
                    sx={{
                      textAlign: 'center',
                      fontSize: 13,
                      color: 'rgba(148,163,184,0.9)',
                    }}
                  >
                    Don&apos;t have an account?{' '}
                    <Link
                      component={RouterLink}
                      to='/register'
                      underline='none'
                      sx={{
                        color: '#1DAFA1',
                        fontWeight: 800,
                        '&:hover': { color: '#23c5b5' },
                      }}
                    >
                      Sign up
                    </Link>
                  </Typography>
                  <Typography
                    sx={{
                      mt: 1,
                      textAlign: 'center',
                      fontSize: 13,
                      color: 'rgba(148,163,184,0.9)',
                    }}
                  >
                    Forgot password?{' '}
                    <Link
                      component={RouterLink}
                      to='/ForgotPassword'
                      underline='none'
                      sx={{
                        color: '#1DAFA1',
                        fontWeight: 800,
                        '&:hover': { color: '#23c5b5' },
                      }}
                    >
                      Forgot password
                    </Link>
                  </Typography>
                </Stack>
              </Box>
            </Paper>

            <Typography
              sx={{
                mt: 3,
                textAlign: 'center',
                fontSize: 12,
                color: 'rgba(100,116,139,1)',
              }}
            >
              By signing in, you agree to our{' '}
              <Link
                component={RouterLink}
                to='#'
                onClick={() => window.open('./assets/Unitic_Exchange_Terms_Of_Use.pdf', '_blank')}
                underline='none'
                sx={{ color: '#1DAFA1', '&:hover': { color: '#23c5b5' } }}
              >
                Terms of Service
              </Link>{' '}
              and{' '}
              <Link
                component={RouterLink}
                to='#'
                onClick={() => window.open('./assets/Unitic_Exchange_Privacy_Policy.pdf', '_blank')}
                underline='none'
                sx={{ color: '#1DAFA1', '&:hover': { color: '#23c5b5' } }}
              >
                Privacy Policy
              </Link>
              .
            </Typography>
          </Box>
        </Box>
      </Box>
    </>
  )
}

export default SignIn
